import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, SlidersHorizontal, X } from "lucide-react";
import api from "@/lib/api";

const SORTS = [
  { id: "featured", label: "Featured" },
  { id: "price-asc", label: "Price: Low to High" },
  { id: "price-desc", label: "Price: High to Low" },
  { id: "newest", label: "Newest" },
];

const SIZES = ["S", "M", "L", "XL", "XXL"];

// Coming-soon placeholder slots. These render the LV-style catalog density
// before the rest of the catalogue ships. Each shows a sketch silhouette + name.
const COMING_SOON = [
  {
    id: "vintage-racer",
    title: "Vintage Racer Jacket",
    subtitle: "Cognac · in atelier",
    image: "https://images.unsplash.com/photo-1551488831-00ddcb6c6bd3?w=1400&q=80",
  },
  {
    id: "shearling-aviator",
    title: "Shearling Aviator",
    subtitle: "Tobacco · in atelier",
    image: "https://images.unsplash.com/photo-1520975916090-3105956dac38?w=1400&q=80",
  },
  {
    id: "slim-bomber",
    title: "Slim Fit Bomber",
    subtitle: "Obsidian · in atelier",
    image: "https://images.unsplash.com/photo-1591047139829-d91aecb6caea?w=1400&q=80",
  },
  {
    id: "heritage-cafe",
    title: "Heritage Cafe Racer",
    subtitle: "Espresso · in atelier",
    image: "https://images.unsplash.com/photo-1542272604-787c3835535d?w=1400&q=80",
  },
];

export default function ShopPage() {
  const [products, setProducts] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [size, setSize] = useState("All");
  const [sort, setSort] = useState("featured");

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data));
  }, []);

  const filtered = useMemo(() => {
    let list = products.filter(
      (p) => size === "All" || p.sizes.includes(size),
    );
    if (sort === "price-asc") list = [...list].sort((a, b) => a.price - b.price);
    if (sort === "price-desc") list = [...list].sort((a, b) => b.price - a.price);
    if (sort === "newest") list = [...list].sort((a, b) => b.index - a.index);
    return list;
  }, [products, size, sort]);

  // Total visible count = live items + coming-soon (acts like LV's full catalog count)
  const totalCount = filtered.length + COMING_SOON.length;

  return (
    <div data-testid="shop-page" className="bg-[#ffffff] min-h-screen">
      {/* Breadcrumb */}
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 pt-24 pb-2">
        <nav
          data-testid="shop-breadcrumb"
          className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-[#000000]/55"
        >
          <Link to="/" className="hover:text-[#000000] transition-colors">
            Home
          </Link>
          <ChevronRight size={12} strokeWidth={1.2} className="opacity-60" />
          <span className="text-[#000000]/85">Men</span>
          <ChevronRight size={12} strokeWidth={1.2} className="opacity-60" />
          <span className="text-[#000000]/85">Ready-to-Wear</span>
          <ChevronRight size={12} strokeWidth={1.2} className="opacity-60" />
          <span className="text-[#000000]">Leather Jackets</span>
        </nav>
      </div>

      {/* Category editorial hero — full-width lifestyle banner, uncropped */}
      <div
        data-testid="shop-category-hero"
        className="w-full bg-[#f5f5f5] overflow-hidden mt-4"
      >
        <img
          src="https://customer-assets-m6fa6gv7.emergentagent.net/job_premium-jackets-6/artifacts/6aov83ja_ChatGPT%20Image%20Sep%204%2C%202026%2C%2012_43_32%20AM.png"
          alt="Boscage — Leather Jackets"
          className="block w-full h-auto"
        />
      </div>

      {/* Title row */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pt-6 pb-10">
        <h1
          data-testid="shop-title"
          className="font-serif text-3xl md:text-4xl tracking-tight text-[#000000]"
        >
          Leather Jackets
        </h1>
        <p className="mt-3 max-w-2xl text-[13px] leading-relaxed text-[#000000]/65">
          BOSCAGE leather outerwear is hand-finished, numbered and registered.
          Each silhouette is produced in limited runs of 80, with lifetime
          care through the Maison Renewal Program.
        </p>
      </section>

      {/* Toolbar — items count · filter · sort */}
      <div
        data-testid="shop-toolbar"
        className="sticky top-20 z-20 bg-[#ffffff]/95 backdrop-blur-md border-y border-[#000000]/10"
      >
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 h-14 flex items-center justify-between">
          <button
            data-testid="filter-toggle"
            onClick={() => setFilterOpen((v) => !v)}
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#000000]/80 hover:text-[#000000] transition-colors"
          >
            <SlidersHorizontal size={14} strokeWidth={1.5} />
            {filterOpen ? "Hide filters" : "Filter"}
          </button>

          <span
            data-testid="shop-item-count"
            className="text-[11px] tracking-[0.22em] uppercase text-[#000000]/65"
          >
            {totalCount} item{totalCount !== 1 ? "s" : ""}
          </span>

          <div className="relative">
            <label
              htmlFor="shop-sort"
              className="text-[11px] tracking-[0.22em] uppercase text-[#000000]/55 mr-2 hidden sm:inline"
            >
              Sort
            </label>
            <select
              id="shop-sort"
              data-testid="shop-sort"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="bg-transparent text-[11px] tracking-[0.22em] uppercase text-[#000000] focus:outline-none cursor-pointer pr-4"
            >
              {SORTS.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Filter row (size) — slides open */}
        {filterOpen && (
          <div
            data-testid="filter-row"
            className="border-t border-[#000000]/10 bg-[#ffffff]"
          >
            <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-6 flex flex-wrap items-center gap-3">
              <span className="text-[10px] tracking-[0.28em] uppercase text-[#000000]/60 mr-2">
                Size
              </span>
              {["All", ...SIZES].map((s) => (
                <button
                  key={s}
                  data-testid={`size-filter-${s.toLowerCase()}`}
                  onClick={() => setSize(s)}
                  className={`text-[11px] tracking-[0.22em] uppercase px-4 py-2 border transition-colors ${
                    size === s
                      ? "border-[#000000] text-[#000000] bg-[#000000]/[0.03]"
                      : "border-[#000000]/20 text-[#000000]/70 hover:border-[#000000]/60 hover:text-[#000000]"
                  }`}
                >
                  {s}
                </button>
              ))}
              {(size !== "All" || sort !== "featured") && (
                <button
                  data-testid="filter-reset"
                  onClick={() => {
                    setSize("All");
                    setSort("featured");
                  }}
                  className="ml-auto inline-flex items-center gap-1 text-[10px] tracking-[0.22em] uppercase text-[#000000]/65 hover:text-[#000000]"
                >
                  <X size={12} strokeWidth={1.5} />
                  Reset
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Catalog grid */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-10">
        <div
          className="grid grid-cols-2 md:grid-cols-4 gap-x-4 md:gap-x-6 gap-y-12"
          data-testid="catalog-grid"
        >
          {filtered.map((p) => (
            <ProductCard key={p.product_id} p={p} />
          ))}
          {COMING_SOON.map((p) => (
            <ComingSoonCard key={p.id} p={p} />
          ))}
        </div>

        {filtered.length === 0 && (
          <p
            data-testid="no-products"
            className="text-center text-[#000000]/55 py-16 text-[11px] tracking-[0.22em] uppercase"
          >
            No live pieces available in this size · check back soon.
          </p>
        )}
      </section>

      {/* Pagination shell — LV-style */}
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 pb-24 mt-10 border-t border-[#000000]/10 pt-10">
        <p className="text-center text-[11px] tracking-[0.22em] uppercase text-[#000000]/60">
          Showing {totalCount} of {totalCount}
        </p>
      </div>
    </div>
  );
}

/* ─────────── LV-style product card with hover-swap ─────────── */
const ProductCard = ({ p }) => (
  <Link
    to={`/product/${p.handle}`}
    data-testid={`shop-card-${p.product_id}`}
    className="group block"
  >
    <div className="relative overflow-hidden bg-[#f5f5f5] aspect-[3/4]">
      {/* Base image (product-only) */}
      <img
        src={p.image}
        alt={p.title}
        data-testid={`shop-card-image-${p.product_id}`}
        className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ease-out group-hover:opacity-0"
      />
      {/* Hover image (model wearing) — falls back to base if none provided */}
      <img
        src={p.model_image || p.image}
        alt=""
        aria-hidden="true"
        data-testid={`shop-card-hover-${p.product_id}`}
        className="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-500 ease-out group-hover:opacity-100"
      />
      {p.compare_at_price && (
        <span
          className="absolute top-3 left-3 z-10 text-[9px] tracking-[0.28em] uppercase text-[#000000] bg-[#ffffff] px-2 py-1"
          data-testid="shop-card-badge"
        >
          New
        </span>
      )}
    </div>
    <div className="mt-4 px-1">
      <h3 className="text-[13px] text-[#000000] leading-tight">{p.title}</h3>
      <p className="text-[12px] text-[#000000]/55 mt-0.5">{p.subtitle}</p>
      <div className="mt-2 flex items-baseline gap-2">
        {p.compare_at_price && (
          <span className="text-[12px] text-[#000000]/40 line-through">
            ${p.compare_at_price.toFixed(0)}
          </span>
        )}
        <span className="text-[12px] text-[#000000]">
          ${p.price.toFixed(0)}
        </span>
      </div>
      {/* Color swatch (LV-style tiny dots) */}
      <div className="mt-2 flex items-center gap-1.5">
        <span
          className="block w-3 h-3 border border-[#000000]/30"
          style={{ background: p.color_hex }}
          title={p.color}
        />
      </div>
    </div>
  </Link>
);

/* ─────────── Placeholder card for forthcoming pieces ─────────── */
const ComingSoonCard = ({ p }) => (
  <div
    data-testid={`shop-placeholder-${p.id}`}
    className="group block cursor-default select-none"
    aria-disabled="true"
  >
    <div className="relative overflow-hidden bg-[#f5f5f5] aspect-[3/4]">
      <img
        src={p.image}
        alt={p.title}
        className="absolute inset-0 w-full h-full object-cover grayscale opacity-60 transition-opacity duration-500 group-hover:opacity-75"
      />
      <div className="absolute inset-0 bg-[#ffffff]/40" />
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-[10px] tracking-[0.32em] uppercase text-[#000000] bg-[#ffffff] px-4 py-2 border border-[#000000]/15">
          Coming soon
        </span>
      </div>
    </div>
    <div className="mt-4 px-1">
      <h3 className="text-[13px] text-[#000000]/70 leading-tight">{p.title}</h3>
      <p className="text-[12px] text-[#000000]/45 mt-0.5">{p.subtitle}</p>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="text-[12px] text-[#000000]/40">—</span>
      </div>
    </div>
  </div>
);
